import React from 'react';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import moment from 'moment';
import { Box, Button, Checkbox, FormControlLabel, MenuItem, Select, TextField, Typography, Divider } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import SaveIcon from '@mui/icons-material/Save';
import InputMask from 'react-input-mask';
import BrazilianNumberInput from '../../../components/brazilian-number-input';
import PageHeader from '../../../components/page-header';
import DefaultDialog from '../../../components/default-dialog';
import { useAmendmentCreateEditState } from './context/amendment-create-edit-context';
import { useAmendmentCreateEditController } from './context/amendment-create-edit-controller';

function CreateEditAmendmentContent() {
    const { contractId, sequentialId } = useAmendmentCreateEditState();
    const {
        amendmentRequest,
        showValidationErrors,
        showConfirmDialog,
        handleOnChange,
        handleDateChange,
        handleSave,
        handleConfirmAddAnother,
        handleCancelAddAnother
    } = useAmendmentCreateEditController();

    return (
        <React.Fragment>
            <Grid item xs={12} md={12} lg={12}>
                <PageHeader
                    title={sequentialId ? "Editar Aditivo" : "Novo Aditivo"}
                    subtitle={sequentialId ? "Atualize as informações do aditivo contratual" : "Cadastre um novo aditivo para o contrato"}
                    breadcrumbs={[
                        { label: 'Contratos', href: '/contratos' },
                        { label: 'Aditivos', href: `/contratos/${contractId}/aditivos` },
                        { label: sequentialId ? 'Editar Aditivo' : 'Novo Aditivo' }
                    ]}
                />
            </Grid>

            <Grid item xs={12} md={12} lg={12}>
                <Paper
                    sx={{
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column'
                    }} elevation={3}
                >
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Grid container spacing={3}>
                            {/* Seção: Identificação do Termo */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                                    Identificação do Termo
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <InputLabel required>Tipo de Aditivo</InputLabel>
                                <Select
                                    fullWidth
                                    name="select-amendment-type"
                                    value={amendmentRequest.amendmentTypeId ?? ''}
                                    onChange={handleOnChange}
                                    error={showValidationErrors && !amendmentRequest.amendmentTypeId}
                                    required
                                >
                                    <MenuItem value="">
                                        <em>Selecionar</em>
                                    </MenuItem>
                                    <MenuItem value={1}>Termo de Rescisão</MenuItem>
                                    <MenuItem value={2}>Termo de Aditivo</MenuItem>
                                    <MenuItem value={3}>Termo de Apostilamento</MenuItem>
                                </Select>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <InputLabel required>Número do Termo</InputLabel>
                                <OutlinedInput
                                    autoComplete='off'
                                    fullWidth
                                    name="input-amendment-number"
                                    error={showValidationErrors && (!amendmentRequest.amendmentNumber || amendmentRequest.amendmentNumber.length === 0)}
                                    value={amendmentRequest.amendmentNumber || ''}
                                    onChange={handleOnChange}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <InputLabel required>Data de Assinatura</InputLabel>
                                <DesktopDatePicker
                                    inputFormat="DD/MM/YYYY"
                                    renderInput={(params) => <TextField {...params} fullWidth autoComplete='off'
                                        error={showValidationErrors && (!amendmentRequest.signDate || amendmentRequest.signDate.length === 0)}
                                        required
                                    />}
                                    value={amendmentRequest.signDate !== undefined ? moment(amendmentRequest.signDate) : null}
                                    onChange={(newValue) => {
                                        handleDateChange('signDate', newValue as moment.Moment | undefined);
                                    }} />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <InputLabel>Data de Publicação</InputLabel>
                                <DesktopDatePicker
                                    inputFormat="DD/MM/YYYY"
                                    renderInput={(params) => <TextField {...params} fullWidth autoComplete='off' error={false} />}
                                    value={amendmentRequest.publicationDate && amendmentRequest.publicationDate.length > 0 ? moment(amendmentRequest.publicationDate) : null}
                                    onChange={(newValue) => {
                                        handleDateChange('publicationDate', newValue as moment.Moment | undefined);
                                    }} />
                            </Grid>

                            {/* Seção: Período de Vigência */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                                    Período de Vigência
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <InputLabel>Início de Vigência</InputLabel>
                                <DesktopDatePicker
                                    inputFormat="DD/MM/YYYY"
                                    renderInput={(params) => <TextField {...params} fullWidth autoComplete='off' error={false} />}
                                    value={amendmentRequest.effectiveDateStart && amendmentRequest.effectiveDateStart.length > 0 ? moment(amendmentRequest.effectiveDateStart) : null}
                                    onChange={(newValue) => {
                                        handleDateChange('effectiveDateStart', newValue as moment.Moment | undefined);
                                    }} />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <InputLabel>Fim de Vigência</InputLabel>
                                <DesktopDatePicker
                                    inputFormat="DD/MM/YYYY"
                                    renderInput={(params) => <TextField {...params} fullWidth autoComplete='off' error={false} />}
                                    value={amendmentRequest.effectiveDateEnd && amendmentRequest.effectiveDateEnd.length > 0 ? moment(amendmentRequest.effectiveDateEnd) : null}
                                    onChange={(newValue) => {
                                        handleDateChange('effectiveDateEnd', newValue as moment.Moment | undefined);
                                    }} />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <InputLabel>Prazo Aditado (Dias)</InputLabel>
                                <OutlinedInput
                                    autoComplete='off'
                                    fullWidth
                                    name="input-additional-deadline-days"
                                    value={amendmentRequest.additionalDeadlineDays || 0}
                                    type="number"
                                    inputProps={{ min: 0 }}
                                    onChange={handleOnChange}
                                />
                            </Grid>

                            {/* Seção: Qualificações */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                                    Qualificações do Termo
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                            </Grid>

                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={amendmentRequest.qualificationAdditionSuppression || false}
                                                onChange={handleOnChange}
                                                name="check-qualification-addition-suppression"
                                            />
                                        }
                                        label="Acréscimo/Supressão"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={amendmentRequest.qualificationValidity || false}
                                                onChange={handleOnChange}
                                                name="check-qualification-validity"
                                            />
                                        }
                                        label="Vigência"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={amendmentRequest.qualificationSupplier || false}
                                                onChange={handleOnChange}
                                                name="check-qualification-supplier"
                                            />
                                        }
                                        label="Fornecedor"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={amendmentRequest.qualificationInformative || false}
                                                onChange={handleOnChange}
                                                name="check-qualification-informative"
                                            />
                                        }
                                        label="Informativo"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={amendmentRequest.qualificationReadjustment || false}
                                                onChange={handleOnChange}
                                                name="check-qualification-readjustment"
                                            />
                                        }
                                        label="Reajuste"
                                    />
                                </Box>
                            </Grid>

                            {/* Seção: Dados do Fornecedor */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                                    Dados do Fornecedor
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3} lg={2}>
                                <InputLabel>Tipo Fornecedor</InputLabel>
                                <Select
                                    fullWidth
                                    value={amendmentRequest.supplierType ?? ''}
                                    name='select-supplier-type'
                                    onChange={handleOnChange}
                                >
                                    <MenuItem value="">
                                        <em>Selecionar</em>
                                    </MenuItem>
                                    <MenuItem value="PJ">Pessoa Jurídica</MenuItem>
                                    <MenuItem value="PF">Pessoa Física</MenuItem>
                                    <MenuItem value="PE">Pessoa Estrangeira</MenuItem>
                                </Select>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <InputLabel>
                                    {amendmentRequest.supplierType === 'PF' ? 'CPF do Fornecedor' :
                                        amendmentRequest.supplierType === 'PE' ? 'Identificação do Fornecedor Estrangeiro' : 'CNPJ do Fornecedor'}
                                </InputLabel>
                                <InputMask
                                    mask={amendmentRequest.supplierType === 'PF' ? '999.999.999-99' :
                                        amendmentRequest.supplierType === 'PE' ? '' : '99.999.999/9999-99'}
                                    name="input-supplier-number-identification"
                                    value={amendmentRequest.supplierNumberIdentification || ''}
                                    onChange={handleOnChange}>
                                    <TextField fullWidth
                                        name="input-supplier-number-identification"
                                        value={amendmentRequest.supplierNumberIdentification || ''}
                                        onChange={handleOnChange} />
                                </InputMask>
                            </Grid>

                            <Grid item xs={12} sm={12} md={5} lg={7}>
                                <InputLabel>Nome do Fornecedor</InputLabel>
                                <OutlinedInput
                                    name="input-supplier-name"
                                    autoComplete='off'
                                    fullWidth
                                    value={amendmentRequest.supplierName || ''}
                                    onChange={handleOnChange}
                                />
                            </Grid>

                            {/* Seção: Valores Financeiros */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                                    Valores Financeiros
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4} lg={2}>
                                <InputLabel>Valor Inicial</InputLabel>
                                <BrazilianNumberInput
                                    value={amendmentRequest?.initialValue || 0}
                                    onChange={(value) => handleOnChange({ target: { name: 'input-initial-value', value } })}
                                    decimals={2}
                                    prefix="R$"
                                    placeholder="0,00"
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4} lg={2}>
                                <InputLabel>Valor Acrescido</InputLabel>
                                <BrazilianNumberInput
                                    value={amendmentRequest?.increaseValue || 0}
                                    onChange={(value) => handleOnChange({ target: { name: 'input-increase-value', value } })}
                                    decimals={2}
                                    prefix="R$"
                                    placeholder="0,00"
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4} lg={2}>
                                <InputLabel>Valor Reduzido</InputLabel>
                                <BrazilianNumberInput
                                    value={amendmentRequest?.decreaseValue || 0}
                                    onChange={(value) => handleOnChange({ target: { name: 'input-decrease-value', value } })}
                                    decimals={2}
                                    prefix="R$"
                                    placeholder="0,00"
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4} lg={2}>
                                <InputLabel>Valor Global</InputLabel>
                                <BrazilianNumberInput
                                    value={amendmentRequest?.globalValue || 0}
                                    onChange={(value) => handleOnChange({ target: { name: 'input-global-value', value } })}
                                    decimals={2}
                                    prefix="R$"
                                    placeholder="0,00"
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4} lg={2}>
                                <InputLabel>Nº Parcelas</InputLabel>
                                <OutlinedInput
                                    autoComplete='off'
                                    fullWidth
                                    name="input-number-installments"
                                    value={amendmentRequest.numberInstallments || 0}
                                    type="number"
                                    inputProps={{ min: 0, max: 1000 }}
                                    onChange={handleOnChange}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4} lg={2}>
                                <InputLabel>Valor Parcela</InputLabel>
                                <BrazilianNumberInput
                                    value={amendmentRequest?.installmentValue || 0}
                                    onChange={(value) => handleOnChange({ target: { name: 'input-installment-value', value } })}
                                    decimals={2}
                                    prefix="R$"
                                    placeholder="0,00"
                                    fullWidth
                                />
                            </Grid>

                            {/* Seção: Descrições e Observações */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                                    Descrições e Observações
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                            </Grid>

                            <Grid item xs={12}>
                                <InputLabel required>Objeto do Termo</InputLabel>
                                <OutlinedInput
                                    autoComplete='off'
                                    multiline
                                    rows={4}
                                    onChange={handleOnChange}
                                    value={amendmentRequest.amendmentObject || ''}
                                    name='input-amendment-object'
                                    error={showValidationErrors && (!amendmentRequest.amendmentObject || amendmentRequest.amendmentObject.length === 0)}
                                    required
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <InputLabel>Fundamento Legal</InputLabel>
                                <OutlinedInput
                                    autoComplete='off'
                                    multiline
                                    rows={3}
                                    onChange={handleOnChange}
                                    value={amendmentRequest.legalBasis || ''}
                                    name='input-legal-basis'
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <InputLabel>Informação Complementar</InputLabel>
                                <OutlinedInput
                                    autoComplete='off'
                                    multiline
                                    rows={3}
                                    onChange={handleOnChange}
                                    value={amendmentRequest.additionalInformation || ''}
                                    name='input-additional-information'
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <InputLabel>Observação Informativa</InputLabel>
                                <OutlinedInput
                                    autoComplete='off'
                                    multiline
                                    rows={3}
                                    onChange={handleOnChange}
                                    value={amendmentRequest.informativeObservation || ''}
                                    name='input-informative-observation'
                                    fullWidth
                                />
                            </Grid>

                            {sequentialId && (
                                <Grid item xs={12}>
                                    <InputLabel>Justificativa (obrigatório para alteração)</InputLabel>
                                    <OutlinedInput
                                        autoComplete='off'
                                        multiline
                                        rows={3}
                                        onChange={handleOnChange}
                                        value={amendmentRequest.justification || ''}
                                        name='input-justification'
                                        fullWidth
                                    />
                                </Grid>
                            )}

                            {/* Botão de Salvar */}
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={handleSave}
                                        endIcon={<SaveIcon />}
                                    >
                                        Salvar
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </LocalizationProvider>
                </Paper>
            </Grid>

            <DefaultDialog
                open={showConfirmDialog}
                title="Adicionar novo termo?"
                message="Deseja adicionar um novo termo contratual?"
                type="question"
                onYesClick={handleConfirmAddAnother}
                onNoClick={handleCancelAddAnother}
                onClose={() => {}}
            />
        </React.Fragment >
    );
}

export default function CreateEditAmendment() {
    return <CreateEditAmendmentContent />;
}
